import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

/**
 * WebMobileCameraView — Snapchat-style in-app camera for mobile web.
 *
 * Renders a live <video> stream via getUserMedia(rear camera) with a
 * three-slot bottom dock: library thumbnail (left), shutter (center),
 * quick-add + (right). Shutter draws the current frame to a <canvas>,
 * encodes to JPEG, and hands a File to `onFiles` — same shape the
 * drop-zone uses, so the existing scan pipeline takes it unchanged.
 *
 * If the browser doesn't expose getUserMedia, or the user denies the
 * permission prompt, we render the provided `Fallback` component
 * (the existing 2-button pick view). That keeps the screen usable
 * even when the live camera path isn't available.
 */

type Permission = 'pending' | 'granted' | 'denied' | 'unsupported';

interface WebMobileCameraViewProps {
  onFiles: (files: File[]) => void;
  /** Rendered when getUserMedia is unavailable or denied. */
  Fallback: React.ComponentType<{
    onFiles: (files: File[]) => void;
  }>;
}

// RN Web renders View as <div>; we still need a real <video> element
// for the live stream and a hidden <input type=file> for the library
// fallback. `React.createElement` on raw tag names sidesteps RN's JSX
// intrinsics typing.
const VideoTag = 'video' as unknown as React.ComponentType<any>;

/**
 * Attach a live MediaStream to a <video> element and kick playback.
 *
 * #166 — this used to be inlined in the getUserMedia callback, but that
 * callback runs while the screen is still in `perm='pending'` (which
 * rendered ONLY a spinner — no <video> in the DOM), so `videoRef.current`
 * was null and the assignment was silently skipped. The stream stayed
 * live (OS "camera on" indicator) but the displayed <video> never got a
 * srcObject → permanently black viewfinder. We now (a) always mount the
 * <video>, and (b) attach via this helper from BOTH the ref callback
 * (fires when the element mounts) and the getUserMedia success path
 * (fires when the stream arrives) — whichever is later wins, and the
 * `srcObject !== stream` guard makes a double-call idempotent.
 *
 * The explicit `play()` is belt-and-suspenders for iOS Safari, which can
 * leave a `muted playsInline autoPlay` element paused until told to play.
 */
function attachStreamToVideo(
  el: HTMLVideoElement | null,
  stream: MediaStream | null,
): void {
  if (!el || !stream) return;
  if (el.srcObject === stream) return;
  el.srcObject = stream;
  const p = el.play();
  if (p && typeof (p as any).catch === 'function') {
    (p as any).catch(() => {
      /* autoplay can reject before a gesture; the stream is attached,
         and onLoadedMetadata + the user's shutter tap retry play. */
    });
  }
}

export const WebMobileCameraView: React.FC<WebMobileCameraViewProps> = ({
  onFiles,
  Fallback,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const libInputRef = useRef<HTMLInputElement | null>(null);
  const [perm, setPerm] = useState<Permission>('pending');

  // Acquire the rear camera on mount. Cancellation guard handles the
  // case where the component unmounts before getUserMedia resolves —
  // without it we'd leak a live track.
  useEffect(() => {
    if (Platform.OS !== 'web') {
      setPerm('unsupported');
      return;
    }
    const md =
      typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!md || typeof md.getUserMedia !== 'function') {
      setPerm('unsupported');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const stream = await md.getUserMedia({
          // `ideal` rather than `exact` — laptops without a rear cam
          // (i.e. you, dev box) still get a stream from the FaceTime
          // camera so the UI is debuggable.
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        // The <video> is already mounted (we render it during 'pending'
        // too, behind the spinner), so this attaches immediately. If for
        // any reason the element isn't there yet, the `setVideoEl` ref
        // callback re-attaches the moment it mounts.
        attachStreamToVideo(videoRef.current, stream);
        setPerm('granted');
      } catch (err: any) {
        if (cancelled) return;
        if (
          err?.name === 'NotAllowedError' ||
          err?.name === 'PermissionDeniedError'
        ) {
          setPerm('denied');
        } else {
          setPerm('unsupported');
        }
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  // Stable ref callback so React doesn't detach/reattach every render.
  // Fires when the <video> mounts; attaches the stream if it's already
  // available (covers the mount-after-stream ordering). #166.
  const setVideoEl = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    attachStreamToVideo(el, streamRef.current);
  }, []);

  const shoot = useCallback(() => {
    const v = videoRef.current;
    if (!v || !streamRef.current) return;
    const w = v.videoWidth;
    const h = v.videoHeight;
    // Track may have been requested before metadata loaded — bail
    // rather than capturing a 0×0 canvas.
    if (!w || !h) return;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `camera_${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });
        onFiles([file]);
      },
      'image/jpeg',
      0.9,
    );
  }, [onFiles]);

  const openLibrary = useCallback(() => {
    if (!libInputRef.current) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.style.display = 'none';
      input.addEventListener('change', () => {
        const files = Array.from(input.files ?? []);
        // Reset so re-picking the same file still fires `change`.
        input.value = '';
        if (files.length) onFiles(files);
      });
      document.body.appendChild(input);
      libInputRef.current = input;
    }
    libInputRef.current.click();
  }, [onFiles]);

  // Denied / unsupported → the pick view. No <video> needed.
  if (perm === 'denied' || perm === 'unsupported') {
    return <Fallback onFiles={onFiles} />;
  }

  // pending OR granted: ALWAYS mount the <video> so the stream can
  // attach the instant it arrives (#166 — previously the element only
  // existed once granted, so the stream had nowhere to land and the
  // feed was black). While pending, the spinner overlays the (black,
  // not-yet-streaming) video; once granted, the dock takes over.
  return (
    <View style={styles.stage} testID="web-mobile-camera">
      <VideoTag
        ref={setVideoEl}
        autoPlay
        playsInline
        muted
        // iOS Safari can leave the element paused after srcObject is set;
        // metadata-ready is the reliable moment to (re)kick playback.
        onLoadedMetadata={() => {
          const v = videoRef.current;
          const p = v?.play?.();
          if (p && typeof (p as any).catch === 'function') {
            (p as any).catch(() => {});
          }
        }}
        style={{
          width: '100%',
          height: '100%',
          // `cover` matches Snapchat — fill the frame even if it crops
          // the edges of the sensor. `contain` would letterbox.
          objectFit: 'cover',
          backgroundColor: '#000',
        }}
      />

      {perm === 'pending' ? (
        <View style={styles.pendingOverlay}>
          <ActivityIndicator color="white" />
          <Text style={styles.pendingText}>Starting camera…</Text>
        </View>
      ) : (
        /* Bottom dock — library (left) · shutter (center). The right slot
           is a spacer that keeps the shutter centered (the single-item
           Quick-add affordance was removed per the MVP backlog). */
        <View style={styles.dock} pointerEvents="box-none">
          <View style={styles.dockRow}>
            <Pressable
              onPress={openLibrary}
              accessibilityRole="button"
              accessibilityLabel="Pick from library"
              testID="camera-library"
              style={({ pressed }) => [
                styles.sideBtn,
                pressed && { opacity: 0.7 },
              ]}
            >
              <MaterialIcons name="photo-library" size={26} color="#fff" />
            </Pressable>

            <Pressable
              onPress={shoot}
              accessibilityRole="button"
              accessibilityLabel="Take photo"
              testID="camera-shutter"
              style={({ pressed }) => [
                styles.shutterOuter,
                pressed && { transform: [{ scale: 0.96 }] },
              ]}
            >
              <View style={styles.shutterInner} />
            </Pressable>

            {/* Right spacer — balances the library button so the shutter
                stays centered. */}
            <View style={styles.sideBtn} />
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  stage: {
    flex: 1,
    backgroundColor: '#000',
    minHeight: 560,
    overflow: 'hidden',
    position: 'relative',
  },
  pendingOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    backgroundColor: '#000',
  },
  pendingText: {
    color: '#fff',
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
  },
  dock: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 28,
    // Subtle dim gradient via flat rgba so the controls stay legible
    // against bright scenes. A real linear gradient would need a
    // dependency or expo-linear-gradient — overkill for v1.
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  dockRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sideBtn: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  shutterOuter: {
    width: 76,
    height: 76,
    borderRadius: 38,
    borderWidth: 4,
    borderColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  shutterInner: {
    width: 58,
    height: 58,
    borderRadius: 29,
    backgroundColor: '#fff',
  },
});

export default WebMobileCameraView;
